export type TierId = 'plus' | 'max' | 'ultra';
export type DurationId = 'monthly' | 'quarterly' | 'yearly';

export interface TierPlan {
  tier: TierId;
  label: string;
  usageLimit: number;     // messages per window
  tokenLimit: number;     // tokens per window
  windowHours: number;    // window size in hours
  description: string;
  highlight?: boolean;    // feature this tier in UI
}

export interface DurationOption {
  id: DurationId;
  label: string;
  durationDays: number;
}

export interface TierPricing {
  tier: TierId;
  duration: DurationId;
  price: number;
}

// ── Tier definitions ──────────────────────────────────────────────────────
export const TIERS: TierPlan[] = [
  { tier: 'plus',  label: 'Plus',  usageLimit: 25,  tokenLimit: 1_500_000, windowHours: 5, description: '25 messages & 1.5M tokens every 5 hours' },
  { tier: 'max',   label: 'Max',   usageLimit: 100, tokenLimit: 6_000_000, windowHours: 5, description: '100 messages & 6M tokens every 5 hours', highlight: true },
  { tier: 'ultra', label: 'Ultra', usageLimit: 200, tokenLimit: 10_000_000, windowHours: 5, description: '200 messages & 10M tokens every 5 hours' },
];

export const DURATIONS: DurationOption[] = [
  { id: 'monthly',   label: 'Monthly',   durationDays: 30  },
  { id: 'quarterly', label: 'Quarterly', durationDays: 90  },
  { id: 'yearly',    label: 'Yearly',    durationDays: 365 },
];

const TIER_DISPLAY_LABELS: Record<string, string> = {
  free: 'Free',
  plus: 'Plus',
  max: 'Max',
  ultra: 'Ultra',
};

const PLAN_DISPLAY_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  annual: 'Yearly',
  weekly: 'Weekly',
};

export function formatTierLabel(tierId: string | null | undefined): string {
  if (!tierId) return TIER_DISPLAY_LABELS.free;
  return TIER_DISPLAY_LABELS[tierId] || tierId;
}

export function formatPlanLabel(planId: string | null | undefined): string {
  if (!planId) return '';
  return PLAN_DISPLAY_LABELS[planId] || planId;
}

export const TIER_PRICING: TierPricing[] = [
  { tier: 'plus',  duration: 'monthly',   price: 20   },
  { tier: 'plus',  duration: 'quarterly', price: 54 },
  { tier: 'plus',  duration: 'yearly',    price: 192  },
  { tier: 'max',   duration: 'monthly',   price: 70  },
  { tier: 'max',   duration: 'quarterly', price: 187  },
  { tier: 'max',   duration: 'yearly',    price: 672 },
  { tier: 'ultra', duration: 'monthly',   price: 180   },
  { tier: 'ultra', duration: 'quarterly', price: 481   },
  { tier: 'ultra', duration: 'yearly',    price: 1728  },
];

export function getPrice(tier: TierId, duration: DurationId): number {
  const pricing = TIER_PRICING.find(p => p.tier === tier && p.duration === duration);
  if (!pricing) throw new Error(`Missing subscription price: ${tier}/${duration}`);
  return pricing.price;
}

const DURATION_MONTHS: Record<DurationId, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/** Savings percent (integer) of a duration vs paying monthly for the same span. 0 for monthly. */
export function getMonthlyDiscountPercent(tier: TierId, duration: DurationId): number {
  const monthlyTotal = getPrice(tier, 'monthly') * DURATION_MONTHS[duration];
  return Math.round((1 - getPrice(tier, duration) / monthlyTotal) * 100);
}

export function getTier(tierId: TierId): TierPlan | undefined {
  return TIERS.find(t => t.tier === tierId);
}

export type PlanId = DurationId;

export interface PlanOption {
  id: PlanId;
  label: string;
  price: number;
  durationDays: number;
  perk: string;
  tier?: TierId;
}

export function buildPlanOption(tier: TierId, duration: DurationId): PlanOption {
  const tierDef = getTier(tier);
  const dur = DURATIONS.find(d => d.id === duration);
  if (!tierDef || !dur) throw new Error(`Invalid subscription plan: ${tier}/${duration}`);
  return {
    id: duration,
    label: `${tierDef.label} ${dur.label}`,
    price: getPrice(tier, duration),
    durationDays: dur.durationDays,
    perk: tierDef.description,
    tier,
  };
}

export type PaymentContextType = 'subscription' | 'router' | 'gift';

export interface PaymentContext {
  type: PaymentContextType;
  planId?: PlanId;
  planLabel?: string;
  durationDays?: number;
  tier?: TierId;
  source?: string;
}
